import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, VarianceIndicator } from "@/components/Kpi";
import { eurShort, num } from "@/lib/format";

/** Classifica dei commerciali: ordinabile, con riga di totale rete. */
export function ClassificaRete({ rows, titolo, testId, scostamentoRete = null,
  scostamentoReteImporto = null }) {
  const [sort, setSort] = useState({ key: "importo_liquidato", dir: -1 });
  const navigate = useNavigate();

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return av > bv ? sort.dir : -sort.dir;
    });
  }, [rows, sort]);

  const tot = rows.reduce((s, r) => ({
    caricato: s.caricato + (r.importo_caricato || 0),
    liquidato: s.liquidato + (r.importo_liquidato || 0),
    budgetLiq: s.budgetLiq + (r.budget_liquidato || 0),
  }), { caricato: 0, liquidato: 0, budgetLiq: 0 });

  const th = (key, label, align = "right") => (
    <th className={`px-6 py-3 text-${align} cursor-pointer select-none hover:text-slate-900 transition-colors`}
      data-testid={`${testId}-sort-${key}`}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))}>
      {label}{sort.key === key ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
    </th>
  );

  return (
    <Card className="overflow-hidden h-full" data-testid={testId}>
      <div className="p-6 pb-3 uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
        {titolo}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm" data-testid={`${testId}-table`}>
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <th className="text-left px-6 py-3">#</th>
              {th("nome", "Commerciale", "left")}
              {th("importo_caricato", "Caricato")}
              {th("importo_liquidato", "Liquidato")}
              {th("raggiungimento_liq_perc", "% raggiung. liquidato")}
              {th("scostamento_liq_perc", "Scost. vs budget liquidato")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.slug} data-testid={`${testId}-row-${r.slug}`}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-200"
                onClick={() => navigate(`/commerciali/${r.slug}`)}>
                <td className="px-6 py-3 font-mono text-slate-400">{i + 1}</td>
                <td className="px-6 py-3 font-medium">{r.nome}</td>
                <td className="px-6 py-3 text-right font-mono">{eurShort(r.importo_caricato)}</td>
                <td className="px-6 py-3 text-right font-mono">{eurShort(r.importo_liquidato)}</td>
                <td className="px-6 py-3 text-right font-mono">
                  {r.raggiungimento_liq_perc === null || r.raggiungimento_liq_perc === undefined
                    ? "n/d" : `${num(r.raggiungimento_liq_perc, 2)}%`}
                </td>
                <td className="px-6 py-3 text-right">
                  <VarianceIndicator value={r.scostamento_liq_perc} />
                  <div className="font-mono text-xs text-slate-400 mt-0.5"
                    data-testid={`${testId}-scost-eur-${r.slug}`}>
                    {r.scostamento_liq_importo === null || r.scostamento_liq_importo === undefined
                      ? "" : `${r.scostamento_liq_importo > 0 ? "+" : ""}${eurShort(r.scostamento_liq_importo)}`}
                  </div>
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold"
              data-testid={`${testId}-total-row`}>
              <td className="px-6 py-3" colSpan={2}>Totale rete</td>
              <td className="px-6 py-3 text-right font-mono">{eurShort(tot.caricato)}</td>
              <td className="px-6 py-3 text-right font-mono">{eurShort(tot.liquidato)}</td>
              <td className="px-6 py-3 text-right font-mono">
                {tot.budgetLiq ? `${num(tot.liquidato / tot.budgetLiq * 100, 2)}%` : "n/d"}
              </td>
              <td className="px-6 py-3 text-right" data-testid={`${testId}-total-scostamento`}>
                <VarianceIndicator value={scostamentoRete === null || scostamentoRete === undefined
                  ? null : Number(scostamentoRete.toFixed(2))} />
                <div className="font-mono text-xs text-slate-400 mt-0.5">
                  {scostamentoReteImporto === null || scostamentoReteImporto === undefined
                    ? "" : `${scostamentoReteImporto > 0 ? "+" : ""}${eurShort(scostamentoReteImporto)}`}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default ClassificaRete;
